import { Box, LoadingOverlay } from '@mantine/core';
import { zIndex } from '@config/zIndex';

type LoadingPageOverlayProps = {
  children: JSX.Element | null;
  visible?: boolean;
  /**
   * Escape hatch for the boot loaders that must outrank the app chrome.
   *
   * Only pass this when the overlay is genuinely covering the WHOLE app and no
   * modal can be open behind it — app bootstrap, the auth gate, the
   * flags/capabilities gate. Anything covering page content must leave the
   * default alone.
   */
  zIndexOverride?: number;
};

/**
 * A loading overlay over a block of page content.
 *
 * ── Why the z-index is not 1000 any more (TV1-2368) ───────────────────────
 *
 * It was, and that put it above Mantine's `modal` (200), `popover` (300) and
 * `overlay` (400) layers — so any consumer that became visible while a modal was
 * open covered the modal, and the user was left looking at a blurred sheet with
 * no way to reach the dialog underneath it.
 *
 * Nothing needed 1000. The three consumers that really do cover the whole app —
 * `Protected`, `Layout`'s capabilities gate, and the flags gate in `routes` —
 * either render INSTEAD of the page (no modal can exist) or are already wrapped
 * in their own `position: fixed; z-index: 2000` container, which creates a
 * stacking context of its own and makes the inner value irrelevant.
 *
 * So the default is now the same modal-safe layer every other content overlay in
 * the app uses, and a caller that genuinely needs to outrank chrome has to say
 * so explicitly. See `@config/zIndex` for the rule.
 */
const LoadingPageOverlay = ({
  children,
  visible = true,
  zIndexOverride
}: LoadingPageOverlayProps ) => {
  return (
    <Box pos="relative">
      <LoadingOverlay
        visible={visible}
        zIndex={zIndexOverride ?? zIndex.contentLoadingOverlay}
        overlayProps={{ radius: 'sm', blur: 2 }}
        loaderProps={{ type: 'ring' }}
      />
      {children}
    </Box>
  );
};

export default LoadingPageOverlay;
